import { BaseFormData } from 'pages/reporting/shared/types'

/**
 * FFS Report Data Types
 *
 * This report generates FFS (Fitness-For-Service) assessments for RAUT pipe indications.
 * For each indication, it fetches the associated ffs-lta-report entry filtered to is_default=true.
 */

// Main info for the FFS report
export interface FfsMainInfo {
  customerName: string
  customerLocation: string
  assetName: string
  assetId: string
  reportDate: string
}

// Cover section data
export interface FfsCoverSectionData {
  reportTitle: string
  preparedBy: string
  preparedFor: string
  reportNumber: string
  revisionNumber: string
}

// AutoCAD drawing section data (optional)
export interface FfsAutocadDrawingData {
  hasDrawing: boolean
  drawingPath?: string
  drawingCaption?: string
}

// Assessment result for a single indication
export interface FfsAssessmentResult {
  indicationId: string
  indicationTitle: string
  indicationSelection: {
    x: { min: number; max: number; unit: string }
    y: { min: number; max: number; unit: string }
  } | null
  ffsCalculationDetails: string // Blank for now
  conclusion: {
    canReachTargetDate: boolean | null
    targetServiceableDate: string | null
    summary: string
  }
  assumptions: string
  recommendations: string
}

// Complete FFS form data state
export interface FfsFormDataState extends BaseFormData {
  mainInfo: FfsMainInfo
  coverSectionData: FfsCoverSectionData
  autocadDrawingData: FfsAutocadDrawingData
  assessmentResults: FfsAssessmentResult[]
  indicationIds: string[] // Input indication IDs
}

// Section IDs for the FFS report
export enum FfsReportSectionId {
  COVER = 'cover',
  AUTOCAD_DRAWING = 'autocadDrawing',
  ASSESSMENT_RESULTS = 'assessmentResults',
  REVIEW = 'review',
  PDF_GENERATION = 'pdfGeneration',
  TABLE_OF_CONTENTS = 'tableOfContents',
}

// Pre-populated data from external sources
export type FfsPrePopulatedData = Partial<
  Pick<
    FfsMainInfo,
    'customerName' | 'customerLocation' | 'assetName' | 'assetId'
  >
>
